import React, { useState, useEffect, useRef } from 'react';
import {
  CreditCard, Plus, Edit2, Trash2, X, Save, Search,
  CheckCircle, XCircle
} from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const PaymentModes = () => {
  const [loading,       setLoading]       = useState(true);
  const [paymentModes,  setPaymentModes]  = useState([]);
  const [filteredModes, setFilteredModes] = useState([]);
  const [showModal,     setShowModal]     = useState(false);
  const [editMode,      setEditMode]      = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [searchTerm,    setSearchTerm]    = useState('');
  const [errors,        setErrors]        = useState({});

  const [formData, setFormData] = useState({
    paymentmodeid: null, paymentmodecode: '', paymentmodename: '',
    description: '', isactive: 1, displayorder: 0,
  });

  const codeRef = useRef(null);

  useEffect(() => { loadPaymentModes(); }, []);
  useEffect(() => { filterPaymentModes(); }, [searchTerm, paymentModes]);

  const loadPaymentModes = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/PaymentModes.php?include_inactive=1');
      if (res.data.success) setPaymentModes(res.data.data || []);
      else alert('Failed to load payment modes');
    } catch { alert('Error loading payment modes.'); }
    finally { setLoading(false); }
  };

  const filterPaymentModes = () => {
    if (!searchTerm) { setFilteredModes(paymentModes); return; }
    const q = searchTerm.toLowerCase();
    setFilteredModes(paymentModes.filter(m =>
      m.paymentmodecode?.toLowerCase().includes(q) ||
      m.paymentmodename?.toLowerCase().includes(q) ||
      m.description?.toLowerCase().includes(q)
    ));
  };

  const handleAdd = () => {
    setFormData({ paymentmodeid: null, paymentmodecode: '', paymentmodename: '', description: '', isactive: 1, displayorder: 0 });
    setErrors({}); setEditMode(false); setShowModal(true);
    setTimeout(() => codeRef.current?.focus(), 100);
  };

  const handleEdit = (mode) => {
    setFormData({ paymentmodeid: mode.paymentmodeid, paymentmodecode: mode.paymentmodecode, paymentmodename: mode.paymentmodename, description: mode.description || '', isactive: mode.isactive, displayorder: mode.displayorder });
    setErrors({}); setEditMode(true); setShowModal(true);
    setTimeout(() => codeRef.current?.focus(), 100);
  };

  const handleDelete = async (mode) => {
    if (!window.confirm(`Are you sure you want to deactivate "${mode.paymentmodename}"?`)) return;
    try {
      const res = await apiClient.delete(`/PaymentModes.php?id=${mode.paymentmodeid}`);
      if (res.data.success) { alert('Payment mode deactivated successfully!'); loadPaymentModes(); }
      else alert(res.data.message || 'Failed to deactivate payment mode');
    } catch (err) { alert('Error: ' + (err.response?.data?.message || err.message)); }
  };

  const handleToggleActive = async (mode) => {
    const newStatus = mode.isactive === 1 ? 0 : 1;
    try {
      const res = await apiClient.put('/PaymentModes.php', { paymentmodeid: mode.paymentmodeid, isactive: newStatus });
      if (res.data.success) { alert(`Payment mode ${newStatus ? 'activated' : 'deactivated'} successfully!`); loadPaymentModes(); }
      else alert(res.data.message || 'Failed to update status');
    } catch (err) { alert('Error: ' + (err.response?.data?.message || err.message)); }
  };

  const handleInputChange = (field, value) => {
    setFormData(p => ({ ...p, [field]: value }));
    if (errors[field]) setErrors(p => ({ ...p, [field]: null }));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.paymentmodecode.trim()) errs.paymentmodecode = 'Payment mode code is required';
    if (!formData.paymentmodename.trim()) errs.paymentmodename = 'Payment mode name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const payload = { paymentmodecode: formData.paymentmodecode.trim().toUpperCase(), paymentmodename: formData.paymentmodename.trim(), description: formData.description.trim(), isactive: formData.isactive, displayorder: parseInt(formData.displayorder) };
      if (editMode) payload.paymentmodeid = formData.paymentmodeid;
      const res = await apiClient[editMode ? 'put' : 'post']('/PaymentModes.php', payload);
      if (res.data.success) {
        alert(`Payment mode ${editMode ? 'updated' : 'created'} successfully!`);
        setShowModal(false); loadPaymentModes();
      } else { alert(res.data.message || `Failed to ${editMode ? 'update' : 'create'} payment mode`); }
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      if (msg.includes('already exists')) setErrors(p => ({ ...p, paymentmodecode: 'Payment mode code already exists' }));
      else alert('Error saving payment mode: ' + msg);
    } finally { setSubmitLoading(false); }
  };

  const inputCls = (err) =>
    `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none
    transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)]
    ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-gray-400 text-lg font-semibold">Loading Payment Modes...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <CreditCard size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Payment Modes Management</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage payment mode configurations</p>
          </div>
        </div>
        <button onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}>
          <Plus size={17} /> Add Payment Mode
        </button>
      </div>

      {/* ── SEARCH ── */}
      <div className="px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3 max-w-[480px]
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by code, name, or description..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* ── TABLE ── */}
      <div className="flex-1 p-5">
        <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
          <table className="w-full border-collapse">
            <thead>
              <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                {['Code', 'Payment Mode Name', 'Description', 'Order', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                    style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredModes.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-16 text-gray-400">
                    <div className="flex flex-col items-center gap-3">
                      {paymentModes.length === 0 ? (
                        <>
                          <CreditCard size={48} className="opacity-20" />
                          <div>No payment modes found. Click "Add Payment Mode" to create one.</div>
                        </>
                      ) : (
                        <>
                          <Search size={48} className="opacity-20" />
                          <div>No payment modes match your search.</div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredModes.map(mode => (
                  <tr key={mode.paymentmodeid}
                    className={`border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b] ${mode.isactive === 0 ? 'opacity-60 bg-gray-50' : ''}`}>
                    <td className="px-4 py-3.5">
                      <span className="px-2.5 py-1 text-white text-[12px] font-bold font-mono rounded tracking-wide"
                        style={{ background: 'linear-gradient(135deg,#3b82f6 0%,#1e40af 100%)' }}>
                        {mode.paymentmodecode}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-gray-800 text-[15px]">{mode.paymentmodename}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">{mode.description || '-'}</td>
                    <td className="px-4 py-3.5 text-center">
                      <span className="inline-block px-3 py-1 bg-gray-100 border border-gray-300 rounded text-sm font-semibold text-gray-600">
                        {mode.displayorder}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <button
                        onClick={() => handleToggleActive(mode)}
                        title={mode.isactive === 1 ? 'Click to deactivate' : 'Click to activate'}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold border-0 cursor-pointer transition-all duration-150 ${
                          mode.isactive === 1
                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}>
                        {mode.isactive === 1
                          ? <><CheckCircle size={13} /> Active</>
                          : <><XCircle size={13} /> Inactive</>}
                      </button>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(mode)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        {mode.isactive === 1 && (
                          <button onClick={() => handleDelete(mode)} title="Deactivate"
                            className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[500px] shadow-2xl border border-gray-200 overflow-hidden"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <CreditCard size={18} />
                {editMode ? 'Edit Payment Mode' : 'Add New Payment Mode'}
              </h3>
              <button onClick={() => setShowModal(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-7">

              {/* Code */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Code <span className="text-red-500">*</span>
                </label>
                <input ref={codeRef} type="text" value={formData.paymentmodecode}
                  onChange={e => handleInputChange('paymentmodecode', e.target.value.toUpperCase())}
                  maxLength={50} placeholder="e.g., CASH, CARD, UPI"
                  className={inputCls(errors.paymentmodecode)} style={{ textTransform: 'uppercase' }} />
                {errors.paymentmodecode && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.paymentmodecode}</p>}
                <p className="text-[11px] text-gray-400 mt-1">Unique code for the payment mode (auto-uppercase)</p>
              </div>

              {/* Name */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Payment Mode Name <span className="text-red-500">*</span>
                </label>
                <input type="text" value={formData.paymentmodename}
                  onChange={e => handleInputChange('paymentmodename', e.target.value)}
                  maxLength={100} placeholder="e.g., Cash Payment, Credit Card"
                  className={inputCls(errors.paymentmodename)} />
                {errors.paymentmodename && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.paymentmodename}</p>}
              </div>

              {/* Display Order */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Display Order</label>
                <input type="number" value={formData.displayorder} min={0} placeholder="0"
                  onChange={e => handleInputChange('displayorder', e.target.value)}
                  className={inputCls(false) + ' text-right'} />
                <p className="text-[11px] text-gray-400 mt-1">Order in which payment mode appears in lists (lower numbers first)</p>
              </div>

              {/* Description */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Description</label>
                <textarea value={formData.description}
                  onChange={e => handleInputChange('description', e.target.value)}
                  placeholder="Optional description or notes" rows={3} maxLength={500}
                  className="w-full px-3.5 py-3 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 resize-y font-[inherit] focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)]" />
              </div>

              {/* Active */}
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                  <input type="checkbox" checked={formData.isactive === 1}
                    onChange={e => handleInputChange('isactive', e.target.checked ? 1 : 0)}
                    className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                  <span className="text-sm font-semibold text-gray-700">Active</span>
                </label>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={() => setShowModal(false)} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  <Save size={16} /> {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default PaymentModes;